import type { EnnismoreTheme } from '@/ui/theme';

const fonts = {
  headline: 'saintecolombe-medium, sans-serif',
  intro: 'decimal-medium, sans-serif',
  subheader: 'decimal-semibold, sans-serif',
  body: 'decimal-light, sans-serif',
};

export const orientExpressTheme: EnnismoreTheme = {
  themeKey: 'orientexpress',
  logoUrl: '/brands/orientexpress/images/logo-generic.png',
  faviconUrl: '/brands/orientexpress/icons/favicon.ico',
  heroImage: {
    src: '/brands/orientexpress/images/hero.jpg',
    alt: 'Orient Express La Minerva',
    width: 1440,
    height: 400,
    mobile: {
      height: 280,
    },
  },
  space: ['0px', '8px', '16px', '20px', '24px', '32px', '40px', '56px'],
  fonts,
  text: {
    heading1: {
      fontFamily: fonts.headline,
      fontSize: '36px',
      lineHeight: '36px',
      textTransform: 'uppercase',
      letterSpacing: '0.72px',
    },
    heading2: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '64px',
      fontWeight: 400,
    },
    heading3: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '48px',
    },
    heading4: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '40px',
    },
    heading5: {
      fontFamily: fonts.intro,
      fontSize: '24px',
      lineHeight: '38px',
    },
    heading6: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '24px',
    },
    intro1: {
      fontFamily: fonts.intro,
      fontSize: '40px',
      lineHeight: '56px',
    },
    intro2: {
      fontFamily: fonts.intro,
      fontSize: '24px',
      lineHeight: '40px',
    },
    subheader1: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '0.48px',
    },
    subheader2: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '32px',
    },
    subheader3: {
      fontFamily: fonts.subheader,
      fontSize: '14px',
      lineHeight: '24px',
    },
    bodyLarge: {
      fontFamily: fonts.body,
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '0.48px',
    },
    bodyCopy: {
      fontFamily: fonts.body,
      fontSize: '16px',
      lineHeight: '28px',
      letterSpacing: '0.32px',
    },
    bodySmall: {
      fontFamily: fonts.body,
      fontSize: '14px',
      lineHeight: '24px',
      fontWeight: 300,
      letterSpacing: '0.28px',
    },
    bodyExtraSmall: {
      fontFamily: fonts.body,
      fontSize: '11px',
      lineHeight: '22px',
    },
    captions: {
      fontFamily: fonts.body,
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
  colors: {
    white: 'white',
    textPrimary: '#041C2C',
    textSecondary: '#999999',
    background: '#F5F5F5',
    primary100: '#f7f7e1',
    primary200: 'white',
    primary300: '#818D95',
    primary400: '#e8e8e8',
    primary500: '#818D95',
    secondary100: '#f6f6f6',
    secondary200: '#d9d9d9',
    secondary300: '#818D95',
    secondary400: '#818D95',
    error: '#B00020',
    sale: '#B00020',
  },
  buttons: {
    primary: {
      color: 'white',
      backgroundColor: '#041C2C',
      fontFamily: fonts.subheader,
      fontSize: '14px',
      borderRadius: '0',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0',
      transition: 'opacity .2s ease-in-out',
      ':hover': {
        opacity: 0.7,
      },
    },
    secondary: {
      color: '#041C2C',
      border: '1px solid #041C2C',
      backgroundColor: 'transparent',
      fontFamily: fonts.subheader,
      fontSize: '14px',
      fontWeight: 600,
      borderRadius: '0',
      textTransform: 'uppercase',
      letterSpacing: '0',
      transition: 'opacity .2s ease-in-out',
      ':hover': {
        opacity: 0.7,
      },
    },
    destructive: {
      color: '#041C2C',
      backgroundColor: 'white',
      border: '1px solid #041C2C',
      fontFamily: fonts.subheader,
      fontSize: '14px',
      borderRadius: 8,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '1.68px',
      transition: 'opacity .2s ease-in-out',
      ':hover': {
        opacity: 0.7,
      },
    },
  },
  keylines: {
    border: '1px solid #E8E4D6',
  },
  forms: {
    heading: {
      color: '#17234E',
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '0.72px',
      fontFamily: fonts.intro,
      textTransform: 'uppercase',
      fontWeight: 500,
      mobile: {
        fontSize: '18px',
      },
    },
    input: {
      fontSize: '14px',
      backgroundColor: 'white',
      borderRadius: '4px',
      border: '1px solid #818D95',
      fontFamily: fonts.intro,
      fontWeight: 500,
    },
    label: {
      fontSize: '14px',
      fontWeight: 300,
      fontFamily: fonts.body,
    },
    radioButton: {
      description: {
        color: '#041C2C',
        letterSpacing: '0.28px',
        lineHeight: '20px',
        padding: '8px 0 0 0',
      },
      collapsibleContent: {
        padding: '8px 0 0 0',
      },
    },
    radioGroup: {
      divider: {
        border: '1px solid #E8E4D6',
      },
    },
    searchForm: {
      backgroundColor: 'transparent',
      display: 'flex',
      justifyContent: 'center',
      divider: {
        display: 'block',
        paddingBottom: '44px',
        marginTop: '44px',
      },
      roomCountSelectorContainer: {
        paddingInlineEnd: '0px',
        marginInlineEnd: ['0', '45px'],
        width: [1, '120px'],
      },
      childAgeSelectorContainer: {
        paddingInlineStart: '0px',
        width: [1, '130px'],
        display: ['grid', 'block'],
        gap: ['20px', '0'],
        gridTemplateColumns: [
          'repeat(auto-fit, minmax(calc(50% - 10px), 1fr))',
          'none',
        ],
      },
      roomOccupancySelectorContainer: {
        paddingInlineEnd: '0px',
        marginInlineEnd: ['0', '45px'],
        width: [1, '300px'],
      },
      roomOccupancySelectorListContainer: {
        width: [1, '475px'],
      },
      datePickerContainer: {
        width: [1, '392px'],
      },
      startDateFieldContainer: {
        width: [1, '186px'],
        marginInlineEnd: '20px',
      },
      endDateFieldContainer: {
        width: [1, '186px'],
      },
    },
  },
  fontFaceDefinitions: [
    {
      fontFamily: 'decimal-light',
      fontWeight: 300,
      sources: {
        woff2: '/brands/orientexpress/fonts/Decimal-Light.woff2',
      },
    },
    {
      fontFamily: 'decimal-normal',
      fontWeight: 400,
      sources: {
        woff2: '/brands/orientexpress/fonts/Decimal-Book.woff2',
      },
    },
    {
      fontFamily: 'decimal-medium',
      fontWeight: 500,
      sources: {
        woff2: '/brands/orientexpress/fonts/Decimal-Medium.woff2',
      },
    },
    {
      fontFamily: 'decimal-semibold',
      fontWeight: 600,
      sources: {
        woff2: '/brands/orientexpress/fonts/Decimal-Semibold.woff2',
      },
    },
    {
      fontFamily: 'saintecolombe-medium',
      fontWeight: 400,
      sources: {
        woff2: '/brands/orientexpress/fonts/SainteColombe-Medium.woff2',
      },
    },
  ],
  componentProperties: {
    confirmation: {
      title: {
        fontSize: '36px',
        textTransform: 'uppercase',
        lineHeight: '42px',
        letterSpacing: '0.72px',
        fontFamily: fonts.headline,
        color: 'white',
        mobile: {
          fontSize: '26px',
        },
      },
      confirmationMessage: {
        color: 'white',
        fontSize: '18px',
      },
      header: {
        backgroundColor: '#713C35',
      },
      bookingConfirmationDetailsCard: {
        manageMyBooking: {
          link: {
            fontSize: '12px',
            textTransform: 'uppercase',
            textDecoration: 'underline',
            letterSpacing: '1.44px',
            fontFamily: fonts.intro,
          },
        },
      },
      bookingConfirmationContainer: {
        divider: {
          borderColor: '#E8E4D6',
        },
      },
      headerImage: {
        height: '480px',
        objectFit: 'cover',
        container: {
          margin: '0',
          width: '100%',
          paddingBottom: '48px',
        },
      },
      anyQuestionsCard: {
        title: {
          mobile: {
            fontSize: '18px',
          },
        },
      },
      restaurantsCard: {
        title: {
          mobile: {
            fontSize: '18px',
          },
        },
      },
      whereToFindUsCard: {
        title: {
          mobile: {
            fontSize: '18px',
          },
        },
        planYourJourneyLink: {
          color: '#1A1A1A',
        },
      },
    },
    consentInputs: {
      title: {
        display: 'none',
      },
    },
    bookingManagement: {
      title: {
        textTransform: 'uppercase',
        fontSize: '36px',
        lineHeight: '42px',
        letterSpacing: '0.72px',
        fontFamily: fonts.headline,
        mobile: {
          fontSize: '28px',
          lineHeight: '34px',
          letterSpacing: '0.56px',
        },
      },
      bookingCard: {
        header: {
          backgroundColor: '#E8E4D6',
          borderBottom: '1px solid #E8E4D6',
          fontFamily: fonts.subheader,
          textTransform: 'none',
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '0.28px',
        },
        cancellationModal: {
          confirmButton: {
            variant: 'primary',
          },
        },
      },
    },
    header: {
      height: 90,
      margin: '0 auto',
      maxWidth: '1280px',
      logo: {
        borderRight: 'none',
        padding: '10px',
      },
      wizard: {
        step: {
          textTransform: 'uppercase',
          fontSize: '12px',
          fontWeight: 500,
          opacity: 0.5,
          fontFamily: fonts.intro,
          letterSpacing: '1.44px',
          active: {
            opacity: 1,
          },
        },
      },
      languageAndCurrencySelector: {
        fontFamily: fonts.body,
        fontSize: '14px',
        letterSpacing: '0.28px',
      },
    },
    footer: {
      backgroundColor: '#E8E4D6',
      color: '#041C2C',
      fontFamily: fonts.intro,
      fontWeight: 500,
      letterSpacing: '0.32px',
      textDecoration: 'none',
      fontSize: '16px',
      copyright: {
        display: 'none',
      },
      link: {
        mobile: {
          gap: '28px',
        },
      },
    },
    searchPage: {
      header: {
        backgroundColor: 'white',
        mobile: {
          backgroundColor: '#E8E4D6',
        },
      },
    },
    search: {
      results: {
        bedroomAvailabilityList: {
          rowGap: '40px',
        },
        sidebarContainer: {
          cancellationPolicy: {
            backgroundColor: 'transparent',
            border: '1px solid #E8E4D6',
            borderRadius: '6px',
            color: '#041C2C',
            lineHeight: '20px',
            title: {
              lineHeight: '30px',
              fontWeight: 500,
              fontFamily: fonts.intro,
            },
            mobile: {
              textTransform: 'uppercase',
              color: '#041C2C',
              fontFamily: fonts.intro,
              letterSpacing: '1.44px',
              fontSize: '12px',
              lineHeight: '12px',
            },
          },
          hotelPerks: {
            backgroundColor: 'hsl(46.67deg 28.12% 87.45% / 40%)',
            border: 'none',
            mobile: {
              backgroundColor: '#E8E4D6',
              title: {
                fontSize: '20px',
              },
              button: {
                textTransform: 'uppercase',
                fontSize: '14px',
                letterSpacing: '1.68px',
                lineHeight: '14px',
              },
            },
          },
        },
      },
      collapsibleForm: {
        searchSummary: {
          border: 'none',
          lineHeight: '38px',
          mobile: {
            padding: '16px',
            title: {
              fontSize: '16px',
            },
          },
          modifySearchButton: {
            textTransform: 'uppercase',
            fontSize: '14px',
            letterSpacing: '1.68px',
            lineHeight: '14px',
            fontFamily: fonts.intro,
            mobile: {
              fontSize: '12px',
              lineHeight: '12px',
              letterSpacing: '1.44px',
            },
          },
        },
      },
    },
    modals: {
      title: {
        textTransform: 'uppercase',
        fontFamily: fonts.headline,
        fontSize: '36px',
        letterSpacing: '0.72px',
        lineHeight: '42px',
        mobile: {
          fontSize: '28px',
          lineHeight: '34px',
          letterSpacing: '0.56px',
        },
      },
      overlay: {
        backdropFilter: 'blur(1px)',
      },
    },
    calendar: {
      color: '#041C2C',
      border: '1px solid #818D95',
      borderRadius: '4px',
      boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25)',
      header: {
        color: '#041C2C',
        backgroundColor: '#E8E4D6',
        borderRadius: '4px 4px 0 0',
        arrow: {
          stroke: '#818D95',
          top: '-1px',
        },
        caption: {
          fontWeight: 500,
          fontFamily: fonts.intro,
          textTransform: 'uppercase',
          letterSpacing: '0.05rem',
          color: '#041C2C',
          fontSize: '16px',
        },
        nav: {
          hover: {
            backgroundColor: 'hsl(204deg 83% 9% / 10%)',
          },
        },
      },
      body: {
        weekDay: {
          color: '#041C2C',
          fontSize: '16px',
          fontWeight: 300,
          textTransform: 'capitalize',
          letterSpacing: '0.02rem',
          fontFamily: fonts.body,
        },
        date: {
          fontSize: '16px',
          margin: '2px',
          hover: {
            backgroundColor: 'hsl(204deg 83% 9% / 10%)',
          },
          selected: { backgroundColor: '#041C2C' },
          selectedMiddle: { backgroundColor: '#E8E4D6' },
        },
      },
    },
    bedroomCard: {
      roomTitle: {
        fontSize: '28px',
        letterSpacing: '0.56px',
        fontWeight: 500,
        fontFamily: fonts.intro,
      },
      roomRateName: {
        fontSize: '18px',
        letterSpacing: '0.36px',
        paddingBottom: '16px',
        fontFamily: 'decimal-normal, sans-serif',
        textTransform: 'uppercase',
      },
      description: {
        letterSpacing: '0.28px',
        lineHeight: '20px',
      },
      totalPrice: {
        fontFamily: fonts.intro,
      },
      roomSpecItem: {
        letterSpacing: '0.28px',
        color: '#818D95',
        value: {
          color: '#041C2C',
        },
      },
      roomSpecItemsList: {
        rowGap: '16px',
        columnGap: '16px',
      },
    },
    sidebarCards: {
      primary: {
        borderRadius: '0px',
        borderColor: 'white',
      },
      secondary: {
        borderRadius: '0px',
        padding: 'm',
        borderColor: 'primary300',
      },
    },
    checkoutSidebar: {
      cancellationPolicy: {
        title: {
          lineHeight: '30px',
        },
        description: {
          color: '#041C2C',
        },
        borderRadius: '6px',
      },
    },
    staySummary: {
      border: 'none',
      title: {
        fontSize: '24px',
        lineHeight: '32px',
        fontFamily: fonts.intro,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.72px',
        mobile: {
          fontSize: '22px',
        },
      },
      summarySection: {
        padding: '28px 24px',
        mobile: {
          padding: '16px 16px',
        },
      },
      dataRow: {
        label: {
          opacity: 1,
        },
        value: {
          fontFamily: fonts.intro,
        },
      },
      dropdown: {
        textTransform: 'uppercase',
        opacity: 1,
        fontSize: '12px',
        fontFamily: fonts.intro,
        letterSpacing: '1.44px',
      },
      divider: {
        borderColor: '#E8E4D6',
      },
    },
    imageSlider: {
      paginator: {
        color: '#041C2C',
      },
    },
    cards: {
      primary: {
        border: 'none',
        divider: {
          borderTop: '1px solid #E8E4D6',
        },
      },
    },
    page: {
      background: '#F5F5F5',
      title: {
        mobile: {
          fontSize: '28px',
          lineHeight: '34px',
          letterSpacing: '0.56px',
          textTransform: 'uppercase',
          fontFamily: fonts.headline,
        },
      },
    },
    hotelPerk: {
      title: {
        fontFamily: fonts.intro,
      },
      description: {
        opacity: 1,
      },
    },
    halfScreenLayout: {
      borderRight: 'none',
    },
    paymentMethods: {
      googlePay: {
        borderRadius: '8px',
      },
      applePay: {
        borderRadius: '8px',
      },
    },
  },

  icons: {
    ADA: '/brands/orientexpress/icons/accessible_rooms.svg',
    BED_SIZE: '/brands/orientexpress/icons/bed_size.svg',
    BREAKFAST: '/brands/orientexpress/icons/breakfast.svg',
    BAR: '/brands/orientexpress/icons/mini_bar.svg',
    DOG: '/brands/orientexpress/icons/pet_friendly.svg',
    CAPACITY: '/brands/orientexpress/icons/room_capacity.svg',
    SIZE: '/brands/orientexpress/icons/room_size.svg',
    ROOM_WITH_VIEW: '/brands/orientexpress/icons/room_view.svg',
    WIFI: '/brands/orientexpress/icons/wifi.svg',
  },
  __temporary: {
    displayCarTransportOnNewLine: true,
  },
};
